<template>
  <zm-dialog
    :visible="visible"
    :custom-class="dialogWrapClass"
    aria-label="Zoom AI Companion"
    show-close
    @close="closeDialog"
  >
    <div class="bp-ai-companion-dialog__body">
      <div class="tip-header">
        <img src="../../assets/images/ai-companion.svg" class="companion-logo" alt="ai companion logo" />
        {{ t('fbc.ai_companion_dialog.full_title') }}
      </div>
      <div class="tip-content">
        {{ t('fbc.ai_companion_dialog.learn_more_dialog.desc') }}
      </div>
      <div data-testid="common-div-7" v-loading="loading" :id="wrapId" class="video-container-wrap" @click="pauseVideo" @touchend="pauseVideo">
        <img
          tabindex="0"
          v-show="!isVideoPlaying && this.player"
          class="video-control play-squircle-fill"
          src="../../assets/images/play.svg"
          @click="changePlayState"
          @keydown.enter="changePlayState"
        />
        <myPlayer ref="videoPlayer" :options="videoOptions" @ready="handleVideoElReady" />
      </div>
      <div class="tip-list">
        <!-- <p class="tip-list__title">{{ t('fbc.ai_companion_dialog.top_features') }}</p> -->
        <div v-for="item in tipList" :key="item" class="tip-list-item">
          <img src="../../assets/images/check-item.svg" icon-class="check-item" />
          <div v-html="item"></div>
        </div>
        <div class="tips-bottom">
          <!-- <div class="divider"></div> -->
          <p>{{ t('fbc.ai_companion_dialog.discover_more') }}</p>
        </div>
      </div>
      <div class="bottom-desc">{{ t('fbc.ai_companion_dialog.detail_desc') }}</div>
    </div>
  </zm-dialog>
</template>

<script>
import { t } from '../../plugins/i18n.js';
import { get, compact } from 'lodash';
import Player from './video.vue';
import { generateRandomString } from '@/utils'
import { USER_LANG } from '@/components/ADDON_LEARN_MORE_DIALOG/stringUtil'

const AICompanionVideo = get(
  window.newBillingConfig,
  'AICompanionVideo',
  'https://static-st1.zoomdev.us/Zoom_AI_Longform_16x9.mp4',
);

export default {
  name: 'AICompanionLearnMoreDialog',
  components: { myPlayer: Player },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      t,
      wrapId: 'billing-common-video-wrap-' + generateRandomString(16),
      loading: true,
      player: null,
      tipList: [
        t('fbc.ai_companion_dialog.learn_more_dialog.tip1'),
        t('fbc.ai_companion_dialog.learn_more_dialog.tip2'),
        t('fbc.ai_companion_dialog.learn_more_dialog.tip3'),
        t('fbc.ai_companion_dialog.learn_more_dialog.tip4'),
        t('fbc.ai_companion_dialog.learn_more_dialog.tip5'),
      ],
      isVideoPlaying: false,
      videoStates: ['play', 'playing', 'pause', 'ended', 'error'],
      videoOptions: {
        autoplay: false,
        controls: false,
        poster: require('@/assets/images/companion-poster.jpg'),
        sources: [
          {
            src: AICompanionVideo,
            type: 'video/mp4',
          },
        ],
      },
      language: USER_LANG,
    };
  },
  computed: {
    isDialogSizeLarge() {
      return ['es-ES', 'de-DE', 'fr-FR', 'pt-PT', 'it-IT', 'vi-VN', 'pl-PL', 'tr-TR', 'id-ID', 'ru-RU', 'nl-NL'].includes(this.language);
    },
    dialogWrapClass() {
      return compact([
        'bp-dialog-base',
        'bp-ai-companion-dialog',
        this.isDialogSizeLarge ? 'bp-dialog-large' : 'bp-dialog-medium',
      ]).join(' ');
    },
  },
  beforeUnmount() {
    this.removeVideoListener();
  },
  mounted () {
    this.$emit('impression');
  },
  methods: {
    handleCloseDialog() {
      this.player?.pause?.();
      this.$emit('closed');
    },
    handleVideoElReady() {
      this.loading = false;
      this.player = this.$refs.videoPlayer.player;
      this.registerVideoEvent();
    },
    changePlayState() {
      const method = this.isVideoPlaying ? 'pause' : 'play';
      this.player[method]?.();
      if (method === 'play') {
        this.$emit('videoStart')
      }
    },
    pauseVideo() {
      if (this.isVideoPlaying) {
        this.player.pause();
      }
    },
    registerVideoEvent() {
      const videoElement = document.querySelector(`#${this.wrapId}.video-container-wrap video`);
      this.videoStates.forEach((state) => videoElement?.addEventListener(state, this.onVideoStateChange));
    },
    removeVideoListener() {
      const videoElement = document.querySelector(`#${this.wrapId}.video-container-wrap video`);
      this.videoStates.forEach((state) => videoElement?.removeEventListener(state, this.onVideoStateChange));
      this.player = null;
    },
    onVideoStateChange({ type }) {
      this.isVideoPlaying = type?.includes('play');
    },
    closeDialog() {
      this.handleCloseDialog();
    },
  },
};
</script>

<style lang="scss">
@import '@/scss/mixins.scss';

.zm-theme--dark {
  --dark-text-color: #f7f9fa;
  --dark-text-color2: #98a0a9;
}

$font-size: 14px;
@mixin commonStyle {
  &__body {
    .video-container-wrap {
      height: 100%;
      grid-area: video;
      position: relative;
      .video-control {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 62px;
        height: 62px;
        transform: translate(-50%, -50%);
        cursor: pointer;
        z-index: 1;
        video {
          width: 100%;
          height: 100%;
          // object-fit: cover;
          pointer-events: none;
        }
        i {
          font-size: 40px;
          color: var(--dark-text-color, #00053d);
        }
      }
      .video-player-wrapper {
        .vjs-control-bar {
          display: none;
        }
      }
    }
    .tip-header {
      grid-area: header;
      color: var(--dark-text-color, #00053d);
      font-size: 22px;
      font-weight: 700;
      display: flex;
      align-items: center;
      height: fit-content;
    }
    .tip-content {
      grid-area: content;
      font-size: 14px;
      color: var(--dark-text-color, #666487);
      font-weight: 400;
      line-height: 21px;
    }
    .tip-list {
      grid-area: list;
      &__title {
        color: var(--day-foreground-foreground-strong, #00031f);
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%; /* 19.2px */
        height: 27px;
      }
      &-item {
        display: flex;
        align-items: baseline;
        line-height: 22px;
        color: var(--dark-text-color, #00053d);
        font-size: 14px;
        font-weight: 400;
        margin-top: 8px;

        b {
          font-weight: 600;
        }

        > img {
          width: 10px;
          height: 10px;
          margin-right: 7px;
          flex-shrink: 0;
        }
      }
      .tips-bottom {
        height: fit-content;
        .divider {
          width: 436px;
          height: 1px;
          background: #e0e0e6;
          margin: 16px 0;
        }
        color: var(--dark-text-color, #00053d);
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;

        p {
          margin: 16px 0;
          font-size: 14px;
        }
      }
    }
    .bottom-desc {
      padding-top: 16px;
      grid-area: bottom;
      color: var(--dark-text-color2, #666487);
      font-size: 14px;
      font-weight: 400;
      border-top: 1px solid var(--dark-text-color2, #e0e0e6);
    }
  }
}
@mixin desktopStyle {
  $right-slide-gap: 32px;
  $dialog-height: 600px;
  $dialog-height-medium: 650px;
  $dialog-height-large: 750px;

  width: 1300px !important;
  padding: 0 !important;
  border-radius: 8px !important;
  border: 0 !important;
  overflow: hidden;

  .zm-dialog__header {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;

    .zm-dialog__title {
      display: none !important;
    }
  }
  .zm-dialog__headerbtn {
    z-index: 100;
  }
  &__body {
    height: $dialog-height;
    display: grid;
    grid-template-columns: auto 500px;
    grid-template-rows: repeat(4, auto);
    grid-template-areas: 'video header' 'video content' 'video list' 'video bottom';
    width: 100%;
    z-index: 90;
    .video-container-wrap {
      width: 800px;
      .video-player-wrapper {
        height: 600px !important;
      }
    }
    .video-js {
      border-radius: 12px 0 0 12px;
    }
    .tip-header {
      margin: $right-slide-gap 0 0 $right-slide-gap;
      .companion-logo {
        width: 44px;
        height: 44px;
        margin-right: 16px;
      }

      .add-on-new {
        margin-left: 8px;
        width: 45px;
        height: 20px;
      }
    }
    .tip-content {
      margin: 0 $right-slide-gap 0 $right-slide-gap;
      height: fit-content;
    }
    .tip-list {
      margin: 0 $right-slide-gap 0 $right-slide-gap;
      height: fit-content;
    }
    .bottom-desc {
      margin: 0 $right-slide-gap 0 $right-slide-gap;
    }
  }
  /** Large Size Dialog */
  &.bp-dialog-large {
    .bp-ai-companion-dialog__body {
      height: $dialog-height-large;
    }
  }
  &.bp-dialog-medium {
    .bp-ai-companion-dialog__body {
      height: $dialog-height-medium;
    }
  }
}
@mixin mobileStyle {
  &__body {
    display: flex;
    flex-direction: column;
    .tip-header {
      .companion-logo {
        width: 32px;
        height: 32px;
        margin-right: 12px;
      }
      .add-on-new {
        margin-left: 8px;
        width: 33px;
        height: 20px;
      }
    }
    .tip-content {
      margin-top: 12px;
    }
    .video-container-wrap {
      height: 193px;
      margin-top: 12px;
    }
    .video-js {
      border-radius: 0 !important;
    }
    .tip-list {
      margin-top: 12px;
    }
  }
}
.bp-ai-companion-dialog {
  @include commonStyle;
  @include media(large-desktop) {
    @include desktopStyle;
  }
  @include media(desktop, tablet, mini-tablet, mobile) {
    @include mobileStyle;
  }
}
</style>
